import React, {
    CSSProperties,
    Dispatch,
    ReactElement,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    AmountUnit,
    QuestsApi,
    QuestConfig,
    QuestReset,
    RewardConfigResponse,
    RewardType,
} from "@devour/client";
import classNames from "classnames";
import CircularProgressIndicator from "../CircularProgress";
import {useDispatch, useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {MdOutlineKeyboardArrowRight} from "react-icons/md";
import {isMobile, isTablet} from "react-device-detect";
import getConfig from "@/utils/getConfig";
import {useGetQuestProgressionForUser} from "@/hooks/useGetQuestProgressionForUser";
import Toast from "@/components/Toast";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetUserLevel} from "@/hooks/useGetUserLevel";
import {store} from "@/redux";
import {updateAccountLevel, updateCurrentUser} from "@/redux/meta/metaActions";
import {BsTriangleFill} from "react-icons/bs";
import AnimateHeight from "react-animate-height";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import {useGetOverwolfSummary} from "@/hooks/useGetOverwolfSummary";
import {
    getTimeUntilQuestReset,
    nextResetTime,
    ResetTimeUnit,
} from "@/utils/devourPlay/getTimeUntilQuestReset";
import {FaRegClock} from "react-icons/fa6";
import useWindowSize from "@/hooks/useWindowSize";
import {useQueryClient} from "@tanstack/react-query";
import {NftPromoModal} from "../modals/NftPromoModal";
import AssetImage from "@/components/AssetImage";
import {isColorDarkOrLight} from "@/utils/isColorDarkOrLight";
import {DateTime} from "luxon";
import Tooltip from "../Tooltip";
import {useGetRewardConfigs} from "@/hooks/useGetRewardConfigs";
import ClaimBadgeButton from "./ClaimBadgeButton";
import { useGetAllQuestConfigsForUser } from "@/hooks/useGetAllQuestConfigsForUser";

interface Props {
    quest: QuestConfig;
    className?: string;
    alwaysMobile?: boolean;
    gameId: string;
    isClaimingInProgress: boolean;
    setIsClaimingInProgress: Dispatch<SetStateAction<boolean>>;
    forceModalDesign?: boolean;
}

const getClaimedRewardsString = (rewards: Array<{
    rewardType: RewardType;
    amount: number,
    amountUnit?: AmountUnit
}>) => {
    const formattedRewards = rewards.map((reward) => `${reward.amountUnit === AmountUnit.FIAT ? "$" : ""}${reward.amount} ${reward.rewardType}`);
    if (formattedRewards.length === 0) {
        return "";
    }

    if (formattedRewards.length === 1) {
        // Single item, return as is
        return `${formattedRewards[0]} successfully claimed!`;
    }

    if (formattedRewards.length === 2) {
        // Two items, join with "and"
        return `${formattedRewards.join(" and ")} successfully claimed!`;
    }

    /*
     * More than two items:
     * Join all but the last two items with commas, then add "and" before the last item.
     */
    const allButLast = formattedRewards.slice(0, -1);
    const last = formattedRewards[formattedRewards.length - 1];
    return `${allButLast.join(", ")}, and ${last} successfully claimed!`;
};

const QuestRewardAmountV2 = (props: {
    name: string,
    amount: number,
    type: RewardType,
    amountUnit?: AmountUnit,
    description?: string,
    forceLighterBg?: boolean,
    iconOnly?: boolean;
}) => {
    const className = props.forceLighterBg
        ? "go-vip-ow_quest-reward-card_info_content_points_toast-lighter"
        : "go-vip-ow_quest-reward-card_info_content_points_toast";

    const iconClass = props.iconOnly ? "go-vip-ow_quest-reward-card_info_content_points_fuel-icon-no-margin" : "go-vip-ow_quest-reward-card_info_content_points_fuel-icon";

    const renderContent = () => {
        switch (props.type) {
            case RewardType.XP:
                return (
                    <div className={className}>
                        <img
                            className={iconClass}
                            src={`${import.meta.env.VITE_CDN_URL}/images/XP-Icon.png`}
                            alt="DevourGO Logo"
                        />
                        {!props.iconOnly && <span
                            className="go-vip-ow_quest-reward-card_info_content_points_toast_text">
                            {props.amount} XP
                        </span>}
                    </div>
                );
            case RewardType.FUEL:
                return (
                    <div className={className}>
                        <img
                            className={iconClass}
                            src={`${import.meta.env.VITE_CDN_URL}/images/FUEL.webp`}
                            alt="DevourGO Logo"
                        />
                        {!props.iconOnly &&
														<span className="go-vip-ow_quest-reward-card_info_content_points_toast_text">
														    {props.amountUnit === AmountUnit.FIAT && "$"}{props.amount}
														</span>
                        }
                    </div>
                );
            case RewardType.NFT:
                return (
                    <div className={className}>
                        <img
                            className={iconClass}
                            src={`${import.meta.env.VITE_CDN_URL}/images/NFT-Icon.png`}
                            alt="DevourGO Logo"
                        />
                        {!props.iconOnly &&
														<span
														    className="go-vip-ow_quest-reward-card_info_content_points_toast_text">{props.name}</span>
                        }
                    </div>
                );
            case RewardType.GIFTCARD:
                return (
                    <div className={className}>
                        <img
                            className={iconClass}
                            src={`${import.meta.env.VITE_CDN_URL}/images/GiftCard-Icon.png`}
                            alt="DevourGO Logo"
                        />
                        {!props.iconOnly &&
														<span
														    className="go-vip-ow_quest-reward-card_info_content_points_toast_text">Gift Card</span>
                        }
                    </div>
                );
            case RewardType.MERCH:
                return (
                    <div className={className}>
                        <img
                            className={iconClass}
                            src={`${import.meta.env.VITE_CDN_URL}/images/Merch-Icon.png`}
                            alt="DevourGO Logo"
                        />
                        {!props.iconOnly &&
														<span
														    className="go-vip-ow_quest-reward-card_info_content_points_toast_text">Merch</span>
                        }
                    </div>
                );
            case RewardType.PROMO:
            case RewardType.PROMOTOKEN:
                return (
                    <div className={className}>
                        <img
                            className={iconClass}
                            src={`${import.meta.env.VITE_CDN_URL}/images/Promo-Icon.png`}
                            alt="DevourGO Logo"
                        />
                        {!props.iconOnly &&
														<span
														    className="go-vip-ow_quest-reward-card_info_content_points_toast_text">{props.name}</span>
                        }
                    </div>
                );
            case RewardType.OTHER:
                return (
                    <div className={className}>
                        <img
                            className={iconClass}
                            src={`${import.meta.env.VITE_CDN_URL}/images/Other-Icon.png`}
                            alt="DevourGO Logo"
                        />
                        {!props.iconOnly &&
														<span
														    className="go-vip-ow_quest-reward-card_info_content_points_toast_text">
														    {props.name || "Other"}
														</span>
                        }
                    </div>
                );

            default:
                return <div/>;
        }
    };

    if (props.description) {
        return (
            <Tooltip
                // containerElementClassname="go-vip-ow_quest_milestones_milestone_description-column"
                outsideContent={renderContent()}
                className="go-vip-ow_quest-reward-card_description-tooltip"
                disablePositioning={false}
                adjustVertical={false}
                tooltipStyle={{width: "fit-content", top: "-10"}}
            >
                <span className="go-vip-ow_quest-reward-card_info_content_points_toast_text"
                    style={{whiteSpace: "nowrap"}}>
                    {props.description}
                </span>
            </Tooltip>
        );
    }

    return renderContent();
};

const MOBILE_BREAKPOINT = 576;
const MEDIUM_BREAKPOINT = 1280;
const LARGE_BREAKPOINT = 1650;

export default function GoVipOWQuestRewardCard(props: Props) {
    const {quest, forceModalDesign = false} = props;
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [claimingToast, setClaimingToast] = useState<{
        message: string;
        success: boolean
    }>(undefined);
    const [isQuestCollapsed, setIsQuestCollapsed] = useState<boolean>(true);
    const milestoneRewardIds = quest.milestones.flatMap((m) => m.rewardConfigIds);
    const allRewardConfigIds = milestoneRewardIds.concat(quest.rewardConfigIds);
    const {data: rewardConfigsRes} = useGetRewardConfigs(allRewardConfigIds);
    const isAuthenticated = !!fullToken;
    const { data: questConfigs } = useGetAllQuestConfigsForUser(
        fullToken,
        isAuthenticated ? {} : undefined,
    );

    // Get quest IDs for progression fetch
    const questConfigIds = questConfigs?.map(q => q.id).filter(Boolean) || [];

    // Fetch quest progressions
    const { data: questProgressions } = useGetQuestProgressionForUser(
        fullToken,
        questConfigIds,
    );

    const {refetch: refetchUserProfile} = useGetUserProfile(fullToken, true);
    const {refetch: refetchSummary} = useGetOverwolfSummary();
    const {refetch: refetchUserLevel} = useGetUserLevel(
        fullToken,
        store.getState().metaStore.currentUser?.user.id,
        true,
    );
    const [promoModalData, setPromoModalData] = useState<
				{ nftUri: string; promoDescriptions: Array<string>; nftMintDate: string } | undefined
    >();
    const [isPromoModalOpen, setIsPromoModalOpen] = useState<boolean>(false);
    const isOnMobile = isMobile && !isTablet || props.alwaysMobile;
    const {isOnOverwolf} = useOverwolfInterop();
    const numOfMilestones = quest.milestones?.length || 0;
    const progression = questProgressions?.find(p => p.questConfigId === quest.id);
    // For Monthly Reset Tag
    const [nextResetTime, setNextResetTime] = useState<nextResetTime | null>(null);
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const queryClient = useQueryClient();

    const windowWidth = useWindowSize()[0];
    const isMediumBreakpoint = windowWidth >= MEDIUM_BREAKPOINT && windowWidth < LARGE_BREAKPOINT;

    const sortedMilestones = useMemo(() => {
        if (quest.requireProgressionOrder) {
            return [...quest.milestones].sort((a, b) => a.progressionOrder - b.progressionOrder);
        }
        const completedMilestones = quest.milestones.filter(milestone => {
            return progression?.progression?.[milestone.id]?.isComplete;
        });
        const incompleteMilestones = quest.milestones.filter(milestone => {
            return !progression?.progression?.[milestone.id]?.isComplete;
        });
        return [...completedMilestones, ...incompleteMilestones];
    }, [progression, quest.milestones]);

    const getRewardBadges = (combineFuelFiatAndFixed = false) => {
        const order = [RewardType.XP, RewardType.FUEL];
        const rewards = rewardConfigsRes?.rewards || [];
        const consolidatedRewards = rewards.reduce((acc, curr) => {
            if (curr.type === RewardType.XP) {
                const existing = acc.find((r) => r.type === curr.type);
                if (existing) {
                    existing.amount += curr.amount;
                } else {
                    acc.push({...curr});
                }
            } else if (curr.type === RewardType.FUEL) {
                const existing = combineFuelFiatAndFixed ? acc.find((r) => r.type === curr.type) : acc.find((r) => r.type === curr.type && r.amountUnit === curr.amountUnit);
                if (existing) {
                    existing.amount += curr.amount;
                } else {
                    acc.push({...curr});
                }
            } else {
                acc.push({...curr});
            }
            return acc;

        }, [] as RewardConfigResponse[]);
        return [...consolidatedRewards].sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
    };

    const showResetWarningColor = () => {
        // Show red reset tag if less than 3 days left
        if (nextResetTime?.unit === ResetTimeUnit.DAY && nextResetTime.value > 3) {
            return false;
        }
        return true;
    };

    const milestoneInfo = useMemo(() => {
        const isMilestoneQuest = quest.milestones.length > 1;
        const progressionArray = Object.values(progression?.progression || {});
        const milestonesCompleted = progressionArray.filter(progression => progression.isComplete).length;
        const milestonesClaimed = progressionArray.filter(progression => progression.claimedAt).length;
        const milestonesClaimable = quest.milestones.filter(m => {
            return m.rewardConfigIds?.length > 0 && progression?.progression?.[m.id]?.isComplete;
        }).length;

        return {
            milestonesCompleted,
            milestonesClaimed,
            isMilestoneQuest,
            hasUnclaimedMilestones: milestonesClaimable > milestonesClaimed,
        };
    }, [quest.milestones, progression]);


    const targetAmount = quest.milestones.reduce((acc, milestone) => {
        return acc + (milestone.targetAmount || milestone.rewardPer);
    }, 0);

    useEffect(() => {
        if (quest.questReset && quest.questReset !== QuestReset.NONE) {
            setNextResetTime(getTimeUntilQuestReset(quest.questReset));

            // Set reset time every 5 mins
            const intervalId = setInterval(() => {
                setNextResetTime(getTimeUntilQuestReset(quest.questReset));
            }, FIVE_MINUTES_MS);

            return () => clearInterval(intervalId);
        }
    }, [quest.questReset]);

    async function claimOverwolfReward(id: string) {
        props.setIsClaimingInProgress(true);
        try {
            const isClaimingPromo = rewardConfigsRes?.rewards?.find((reward) => reward.type === RewardType.NFT);
            if (isClaimingPromo) {
                // opens modal to display loading screen
                setIsPromoModalOpen(true);
            }
            const res = await new QuestsApi(getConfig(fullToken)).claimQuestReward({
                id: id,
            });
            await refetchSummary();
            const rewardsWithoutPromo = res.rewards.filter((reward) => reward.rewardType !== RewardType.NFT);
            const rewardWithPromo = res.rewards.find((reward) => reward.rewardType === RewardType.NFT);
            if (rewardsWithoutPromo.length > 0) {
                setClaimingToast({
                    message: getClaimedRewardsString(rewardsWithoutPromo),
                    success: true,
                });
            }
            if (rewardWithPromo) {
                setPromoModalData({
                    nftUri: rewardWithPromo.nftUri,
                    promoDescriptions: rewardWithPromo.promoDescriptions,
                    nftMintDate: rewardWithPromo.nftMintDate,
                });
            }
            queryClient.invalidateQueries({queryKey: ["quest-progression-user"]});
            queryClient.invalidateQueries({queryKey: ["transactions-paginated"]});
            void updateUserLevel();
        } catch (e) {
            console.log("error claiming overwolf reward", e);
            setClaimingToast({
                message: "There has been a problem claiming your reward. Please try again later!",
                success: false,
            });
            setIsPromoModalOpen(false);
        } finally {
            props.setIsClaimingInProgress(false);
        }
    }

    async function updateUserLevel() {
        const [updatedUserProfile, updatedUserLevel] = await Promise.all([refetchUserProfile(), refetchUserLevel()]);
        if (updatedUserProfile.data) {
            dispatch(updateCurrentUser(updatedUserProfile.data));
        }

        if (updatedUserLevel.data) {
            dispatch(updateAccountLevel(updatedUserLevel.data));
        }
    }

    function handleMilestoneClaimButtonClick(e: React.MouseEvent<HTMLAnchorElement>) {
        const container = (e.target as any).closest(".go-vip-ow_quest-reward-card_main");
        setIsQuestCollapsed(false);
        setTimeout(() => {
            container.parentNode
                .querySelector(".go-vip-ow_quest_milestones .go-vip-ow_quest-reward-card_claim-btn")
                .scrollIntoView({
                    behavior: "smooth",
                });
        }, 80);
    }

    const currentProgress = useMemo(() => {
        if (!progression) {
            return 0;
        }
        return Object.entries(progression.progression).reduce((acc, [milestoneId, progressionMilestone]) => {
            const milestone = quest.milestones.find(m => m.id === milestoneId);
            if (milestone?.targetAmount || !milestone?.rewardPer) {
                return acc + progressionMilestone.amount;
            }
            // Target amount is unlimited. Use remainder of progression.
            const remainder = progressionMilestone.amount % milestone.rewardPer;
            return acc + remainder;

        }, 0);
    }, [progression?.progression]);


    const getCircularProgressGlow = () => {
        return progression && progression.isComplete && !progression.claimedAt;
    };

    const renderResetTag = (top: boolean = false) => {
        if (!nextResetTime || quest?.questReset === QuestReset.NONE || !progression) return null;

        return (
            <div
                className={classNames("go-vip-ow_quest-reward-card_reset-tag", {
                    top: top,
                    urgent: showResetWarningColor(),
                })}
            >
                <FaRegClock/> Resets in {nextResetTime.str}{" "}
                {milestoneInfo.hasUnclaimedMilestones && showResetWarningColor() && "- Claim your XP!"}
            </div>
        );
    };

    function renderIcon(): ReactElement {
        if (milestoneInfo.isMilestoneQuest) {
            return (
                <BsTriangleFill
                    className={classNames(
                        "go-vip-ow_quest-reward-card_info_icon",
                        "go-vip-ow_quest_milestones_collapse-icon",
                        {
                            "go-vip-ow_quest_milestones_collapse-icon_flipped": !isQuestCollapsed,
                        },
                    )}
                    onClick={() => setIsQuestCollapsed(!isQuestCollapsed)}
                />
            );
        }
    }

    function getToastStyle(): CSSProperties {
        if (isOnMobile && isOnOverwolf) {
            return {
                maxWidth: "100vw",
                width: "100vw",
                borderRadius: "0",
                marginBottom: "0",
                marginLeft: "0",
            };
        }

        return {
            marginBottom: "0",
        };
    }

    function getQuestEndDate(): string {
        const dt = DateTime.fromMillis(quest?.endAt, {zone: "America/New_York"});
        return `${dt.toFormat("M/d/yy h:mma")} EST`;
    }

    const shouldUseMilestoneOnlyStyle = useMemo(() => {
        // is it a milestone quest? Are there multiple types of rewards?
        const hasMultipleTypes = new Set(rewardConfigsRes?.rewards.map((r) => r.type)).size > 1;
        return hasMultipleTypes && milestoneInfo.isMilestoneQuest;
    }, [quest.milestones, rewardConfigsRes?.rewards]);

    return (
        <>
            <Toast
                message={claimingToast?.message}
                isOpen={!!claimingToast}
                onDismiss={() => setClaimingToast(undefined)}
                removeMarginAdjustment={true}
                className={classNames("overwolf-govip_rewards_claim", {
                    "overwolf-govip_rewards_claim_in-game": isOnMobile && isOnOverwolf,
                })}
                forceDarkMode={true}
                variant={claimingToast?.success ? "success" : "error"}
                style={isOnOverwolf && getToastStyle()}
            />
            <NftPromoModal
                loading={!promoModalData}
                dateMinted={new Date(promoModalData?.nftMintDate)}
                isOpen={isPromoModalOpen}
                onClose={() => setIsPromoModalOpen(false)}
                image={promoModalData?.nftUri}
                description={
                    promoModalData?.promoDescriptions.length === 1
                        ? promoModalData?.promoDescriptions[0]
                        : promoModalData?.promoDescriptions.map((desc) => `- ${desc}`).join("\n")
                }
            />

            <div
                className={classNames("go-vip-ow_quest-reward-card", {
                    disabled: !fullToken,
                    "bottom-spacing": !isQuestCollapsed,
                    "has-sponsorship": props.quest.isSponsored,
                    "has-reward-image": props.quest.rewardImage,
                })}
                style={{
                    borderColor: props.quest.isSponsored && props.quest.color,
                }}
            >
                {props.quest.isSponsored &&
                    <div
                        className={`go-vip-ow_quest-reward-card_sponsor ${isColorDarkOrLight(props.quest.color)}`}
                        style={{
                            backgroundColor: props.quest.color,
                        }}
                    >
                        <AssetImage
                            assetId={props.quest.logo}
                            alt={props.quest.sponsorName}
                            className="go-vip-ow_quest-reward-card_sponsor_logo"
                        />
                        {props.quest.externalUrl &&
                            <a
                                href={props.quest.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="go-vip-ow_quest-reward-card_sponsor_url"
                            >
                                <span>Visit</span>
                                <MdOutlineKeyboardArrowRight />
                            </a>
                        }
                    </div>
                }
                <div className="go-vip-ow_quest-reward-card_main">
                    <div className="go-vip-ow_quest-reward-card_details">
                        {props.quest?.isSponsored &&
                            <div className="go-vip-ow_quest-reward-card_sponsor-info">
                                <div className="go-vip-ow_quest-reward-card_sponsor-info_titles">
                                    <strong>{props.quest?.sponsorName}</strong>
                                    {props.quest.endAt &&
                                        <p className="go-vip-ow_quest-reward-card_info_content_description">
                                            Ends: {getQuestEndDate()}
                                        </p>
                                    }
                                </div>
                                {isOnMobile && props.quest.rewardImage && <div className="go-vip-ow_quest-reward-card_sponsor-info_image">
                                    <AssetImage assetId={props.quest.rewardImage} alt={props.quest.name} />
                                </div>}
                            </div>
                        }
                        <div className="go-vip-ow_quest-reward-card_info">
                            <CircularProgressIndicator
                                showGlow={getCircularProgressGlow()}
                                total={targetAmount}
                                current={currentProgress}
                                colorOverride={props.quest.isSponsored && props.quest.color}
                            />
                            <div className="go-vip-ow_quest-reward-card_info_content">
                                <strong className="go-vip-ow_quest-reward-card_info_content_title">
                                    {props.quest.name}
                                </strong>
                                <p className="go-vip-ow_quest-reward-card_info_content_description">
                                    {props.quest.description}
                                </p>
                                {windowWidth > MOBILE_BREAKPOINT && !forceModalDesign &&
																		<div className="go-vip-ow_quest-reward-card_info_content_points">
    Rewards
																		    {getRewardBadges(shouldUseMilestoneOnlyStyle).map((rewardConfig) => {
																		        return (
																		            <QuestRewardAmountV2
																		                name={rewardConfig.name}
																		                amount={rewardConfig.amount}
																		                type={rewardConfig.type}
																		                amountUnit={rewardConfig.amountUnit}
																		                iconOnly={shouldUseMilestoneOnlyStyle}
																		            />
																		        );
																		    })}
																		</div>
                                }
                            </div>
                            {renderIcon()}
                        </div>
                        <div>{renderResetTag(true)}</div>
                        {(windowWidth <= MOBILE_BREAKPOINT || forceModalDesign) &&
														<div
														    className="go-vip-ow_quest-reward-card_info_content_points"
														    style={{
														        marginTop: forceModalDesign ? "0.6rem" : undefined,
														        fontSize: "0.72rem",
														        flexDirection: shouldUseMilestoneOnlyStyle ? "row" : "column",
														        alignItems: shouldUseMilestoneOnlyStyle ? "center" : "flex-start",
														        paddingLeft: forceModalDesign ? undefined : "1rem",
														    }}
														>
    Rewards
														    {getRewardBadges(shouldUseMilestoneOnlyStyle).map((rewardConfig) => {
														        return (
														            <QuestRewardAmountV2
														                name={rewardConfig.name}
														                amount={rewardConfig.amount}
														                type={rewardConfig.type}
														                amountUnit={rewardConfig.amountUnit}
														                iconOnly={shouldUseMilestoneOnlyStyle}
														            />
														        );
														    })}
														</div>
                        }
                    </div>

                    {fullToken && !isMediumBreakpoint &&
                        <div className="go-vip-ow_quest-reward-card_action">
                            <ClaimBadgeButton
                                type="quest"
                                questId={quest.id}
                                isComplete={progression?.isComplete || false}
                                claimedAt={progression?.claimedAt}
                                hasUnclaimedMilestones={milestoneInfo.hasUnclaimedMilestones}
                                hasRootLevelRewards={quest.rewardConfigIds?.length > 0}
                                allMilestonesClaimed={milestoneInfo.milestonesClaimed === numOfMilestones && numOfMilestones > 0}
                                isCollapsed={isQuestCollapsed}
                                onClaim={claimOverwolfReward}
                                onUncollapseAndScroll={handleMilestoneClaimButtonClick}
                                isClaimingInProgress={props.isClaimingInProgress}
                                windowWidth={windowWidth}
                                forceModalDesign={forceModalDesign}
                            />
                        </div>
                    }

                    {props.quest.rewardImage && !isOnMobile &&
												<div className="go-vip-ow_quest-reward-card_reward-image">
												    <div className="go-vip-ow_quest-reward-card_reward-image_fade"/>
												    <AssetImage assetId={props.quest.rewardImage} alt={props.quest.name}/>
												</div>
                    }
                </div>

                {fullToken && isMediumBreakpoint &&
                    <div className="go-vip-ow_quest-reward-card_action go-vip-ow_quest-reward-card_action--medium-breakpoint">
                        <ClaimBadgeButton
                            type="quest"
                            questId={quest.id}
                            isComplete={progression?.isComplete || false}
                            claimedAt={progression?.claimedAt}
                            hasUnclaimedMilestones={milestoneInfo.hasUnclaimedMilestones}
                            hasRootLevelRewards={quest.rewardConfigIds?.length > 0}
                            allMilestonesClaimed={milestoneInfo.milestonesClaimed === numOfMilestones && numOfMilestones > 0}
                            isCollapsed={isQuestCollapsed}
                            onClaim={claimOverwolfReward}
                            onUncollapseAndScroll={handleMilestoneClaimButtonClick}
                            isClaimingInProgress={props.isClaimingInProgress}
                            windowWidth={windowWidth}
                            forceModalDesign={forceModalDesign}
                        />
                    </div>
                }

                {(numOfMilestones > 1 || milestoneInfo.hasUnclaimedMilestones) &&
										<AnimateHeight duration={300} height={isQuestCollapsed ? 0 : "auto"}>
										    <div className="go-vip-ow_quest_milestones">
										        <div className="go-vip-ow_quest_milestones_progress">
										            {sortedMilestones.map((milestone, i) =>
										                <div
										                    className={classNames("go-vip-ow_quest_milestones_progress_marker", {
										                        completed: progression?.progression[milestone.id]?.amount >= milestone.targetAmount,
										                    })}
										                    key={`${quest.id}-marker-${i}`}
										                />)}
										            <div
										                className="go-vip-ow_quest_milestones_progress_bar"
										                style={{
										                    height: `${milestoneInfo.milestonesCompleted / numOfMilestones * 100}%`,
										                }}
										            />
										        </div>
										        <div className="go-vip-ow_quest_milestones_list">
										            {sortedMilestones.map((milestone, index) =>
										                <React.Fragment key={`${quest.id}-${index}`}>
										                    {(windowWidth <= MOBILE_BREAKPOINT || forceModalDesign) &&
																						<div
																						    className="go-vip-ow_quest_milestones_milestone"
																						>
																						    <div
																						        className="go-vip-ow_quest_milestones_milestone_description-row">
																						        <div
																						            className="go-vip-ow_quest_milestones_milestone_description-row_name">
																						            {milestone.name}
																						        </div>
																						        <div
																						            className="go-vip-ow_quest_milestones_milestone_description-row_points">
																						            {milestone.rewardConfigIds.map((rewardConfigId) => {
																						                if (!rewardConfigsRes) return null;
																						                const rewardConfig = rewardConfigsRes.rewards.find((reward) => reward.id === rewardConfigId);
																						                return (
																						                    <QuestRewardAmountV2
																						                        amount={rewardConfig.amount}
																						                        type={rewardConfig.type}
																						                        amountUnit={rewardConfig.amountUnit}
																						                        name={rewardConfig.name}
																						                        description={rewardConfig.description}
																						                        forceLighterBg={true}
																						                    />
																						                );
																						            })}
																						        </div>
																						    </div>
																						    <div
																						        className="go-vip-ow_quest_milestones_milestone_claim-button">
																						        <ClaimBadgeButton
																						            type="milestone"
																						            questId={quest.id}
																						            milestoneId={milestone.id}
																						            isComplete={progression?.progression?.[milestone.id]?.isComplete || false}
																						            claimedAt={progression?.progression?.[milestone.id]?.claimedAt}
																						            onClaim={claimOverwolfReward}
																						            isClaimingInProgress={props.isClaimingInProgress}
																						            windowWidth={windowWidth}
																						            forceModalDesign={forceModalDesign}
																						        />
																						    </div>
																						</div>
										                    }
										                    {windowWidth > MOBILE_BREAKPOINT && !forceModalDesign &&
																						<div
																						    className="go-vip-ow_quest_milestones_milestone"
																						    style={{display: "flex"}}
																						>
																						    <div
																						        className="go-vip-ow_quest_milestones_milestone_description-column">
																						        <div
																						            className="go-vip-ow_quest_milestones_milestone_description-column_name">
																						            {milestone.name}
																						        </div>
																						        <div
																						            className="go-vip-ow_quest_milestones_milestone_description-column_points">
																						            {milestone.rewardConfigIds.map((rewardConfigId) => {
																						                if (!rewardConfigsRes) return null;
																						                const rewardConfig = rewardConfigsRes.rewards.find((reward) => reward.id === rewardConfigId);
																						                return (
																						                    <QuestRewardAmountV2
																						                        key={rewardConfig.id}
																						                        amount={rewardConfig.amount}
																						                        type={rewardConfig.type}
																						                        amountUnit={rewardConfig.amountUnit}
																						                        name={rewardConfig.name}
																						                        description={rewardConfig.description}
																						                        forceLighterBg={true}
																						                    />
																						                );
																						            })}
																						        </div>
																						    </div>
																						    <div
																						        className="go-vip-ow_quest_milestones_milestone_claim-button">
																						        <ClaimBadgeButton
																						            type="milestone"
																						            questId={quest.id}
																						            milestoneId={milestone.id}
																						            isComplete={progression?.progression?.[milestone.id]?.isComplete || false}
																						            claimedAt={progression?.progression?.[milestone.id]?.claimedAt}
																						            onClaim={claimOverwolfReward}
																						            isClaimingInProgress={props.isClaimingInProgress}
																						            windowWidth={windowWidth}
																						            forceModalDesign={forceModalDesign}
																						        />
																						    </div>
																						</div>
										                    }
										                    {index !== numOfMilestones - 1 &&
																						<div className="go-vip-ow_quest_milestones_divider"/>
										                    }
										                </React.Fragment>)}
										        </div>
										    </div>
										</AnimateHeight>
                }
            </div>
        </>
    );
}
